import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { FileText, Download, Calendar, Plus, Trash2, Play, Clock, X } from 'lucide-react';

const ReportBuilder = () => {
  const [reportConfigs, setReportConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [newReport, setNewReport] = useState({
    name: '',
    type: 'SALES',
    description: '',
    schedule: ''
  });

  useEffect(() => {
    fetchReportConfigs();
  }, []);

  const fetchReportConfigs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/reports/configs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReportConfigs(data.configs || []);
      }
    } catch (error) {
      console.error('Error fetching report configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const createReport = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/reports/configs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newReport)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setNewReport({ name: '', type: 'SALES', description: '', schedule: '' });
        fetchReportConfigs();
        alert('Report configuration created successfully');
      } else {
        alert('Failed to create report configuration');
      }
    } catch (error) {
      console.error('Error creating report:', error);
      alert('Error creating report configuration');
    }
  };

  const generateReport = async (configId, parameters = {}) => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/reports/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ configId, parameters })
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedReport(data.reportRun);
        alert('Report generated successfully');
      } else {
        alert('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const exportReport = (reportRun) => {
    if (!reportRun.result) {
      alert('No report data to export');
      return;
    }

    const data = typeof reportRun.result === 'string' 
      ? JSON.parse(reportRun.result) 
      : reportRun.result;

    // Convert to CSV
    let csv = '';
    if (data.orders) {
      csv = 'Order Number,Date,Customer,Total,Items\n';
      data.orders.forEach(order => {
        csv += `${order.orderNumber},${order.date},${order.customer},${order.total},${order.items}\n`;
      });
    } else if (data.products) {
      csv = 'Product Name,SKU,Stock,In Stock,Total Sold\n';
      data.products.forEach(product => {
        csv += `${product.name},${product.sku},${product.stockQuantity},${product.inStock},${product.totalSold}\n`;
      });
    } else if (data.customers) {
      csv = 'Name,Email,Phone,Total Orders,Total Spent,Last Order\n';
      data.customers.forEach(customer => {
        csv += `${customer.name},${customer.email},${customer.phone},${customer.totalOrders},${customer.totalSpent},${customer.lastOrderDate}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <FileText className="h-6 w-6 mr-2 text-[#d44100]" />
            Advanced Reports
          </h2>
          <p className="text-sm text-gray-600 mt-1">Create and manage custom reports</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Report
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportConfigs.map((config) => (
            <div key={config.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{config.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{config.description || 'No description'}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${
                  config.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {config.type}
                </span>
              </div>

              {config.schedule && (
                <div className="flex items-center text-sm text-gray-600 mb-4">
                  <Clock className="h-4 w-4 mr-2" />
                  Scheduled: {config.schedule}
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => generateReport(config.id)}
                  disabled={generating}
                  className="flex-1 px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50 flex items-center justify-center"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Generate
                </button>
                {config.reportRuns && config.reportRuns.length > 0 && (
                  <button
                    onClick={() => {
                      const latestRun = config.reportRuns[0];
                      if (latestRun.status === 'COMPLETED') {
                        exportReport(latestRun);
                      }
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>

              {config.reportRuns && config.reportRuns.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-gray-600 mb-2">Recent Runs:</p>
                  {config.reportRuns.slice(0, 3).map((run) => (
                    <div key={run.id} className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">
                        {new Date(run.startedAt).toLocaleString()}
                      </span>
                      <span className={`px-2 py-1 rounded ${
                        run.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        run.status === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {run.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Create New Report</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Name</label>
                <input
                  type="text"
                  value={newReport.name}
                  onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                  placeholder="Sales Report"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                <select
                  value={newReport.type}
                  onChange={(e) => setNewReport({ ...newReport, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                >
                  <option value="SALES">Sales Report</option>
                  <option value="INVENTORY">Inventory Report</option>
                  <option value="CUSTOMER">Customer Report</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newReport.description}
                  onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                  rows={3}
                  placeholder="Report description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Schedule (Cron Expression - Optional)</label>
                <input
                  type="text"
                  value={newReport.schedule}
                  onChange={(e) => setNewReport({ ...newReport, schedule: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                  placeholder="0 0 * * * (Daily at midnight)"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createReport}
                className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
              >
                Create Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Results Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Report Results</h3>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {selectedReport.result && (
              <div className="space-y-4">
                <button
                  onClick={() => exportReport(selectedReport)}
                  className="flex items-center px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export to CSV
                </button>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm overflow-x-auto">
                    {JSON.stringify(
                      typeof selectedReport.result === 'string' 
                        ? JSON.parse(selectedReport.result) 
                        : selectedReport.result,
                      null,
                      2
                    )}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportBuilder;

